/**
 * The Dawn Report (5.4a) — a parchment scroll shown ONCE on login summarizing what happened while the
 * captain was away (raids suffered, sales settled, the daily bonus, streak + season progress). Server
 * builds it (S_DAWN); this just renders it and dismisses. Aged paper + wax seal, never dark-glass.
 */
import type { Net } from './net.js';
import type { Audio } from './audio.js';

const INK = '#3b2712', INK_SOFT = '#5c451f', SEP = 'rgba(92,62,28,.30)', SERIF = "'Iowan Old Style','Palatino Linotype','Book Antiqua',Palatino,Georgia,'Times New Roman',serif";

const CSS = `
#dawnwrap { position:fixed; inset:0; z-index:44; display:none; align-items:center; justify-content:center; background:rgba(2,8,16,.6); pointer-events:auto; }
#dawnwrap.on { display:flex; animation:dawnIn .5s ease-out; }
@keyframes dawnIn { from { opacity:0 } to { opacity:1 } }
#dawncard { width:min(92vw,400px); max-height:80vh; display:flex; flex-direction:column; color:${INK}; font-family:${SERIF};
  background:#e8d9b0 url('/ui/parchment.webp') center/100% 100% no-repeat; border-radius:6px;
  box-shadow:0 22px 60px rgba(0,6,12,.7), inset 0 0 40px rgba(90,56,20,.28); overflow:hidden; }
#dawncard .dh { display:flex; align-items:center; gap:9px; padding:15px 18px 11px; border-bottom:1.5px solid ${SEP}; }
#dawncard .dh .seal { width:28px; height:28px; background:url('/ui/waxseal.webp') center/contain no-repeat; flex:none; filter:drop-shadow(0 1px 2px rgba(0,0,0,.4)); }
#dawncard .dh b { font-size:17px; letter-spacing:1px; color:${INK}; font-weight:800; text-transform:uppercase; text-shadow:0 1px 0 rgba(255,250,235,.5); }
#dawncard .dsub { padding:8px 18px 2px; font-size:12px; font-style:italic; color:${INK_SOFT}; }
#dawncard .dbody { overflow:auto; padding:8px 18px 6px; }
#dawncard .dline { display:flex; gap:8px; padding:7px 2px; border-bottom:1px solid ${SEP}; font-size:13px; line-height:1.35; }
#dawncard .dline:last-child { border-bottom:0; }
#dawncard .dnone { text-align:center; font-style:italic; color:${INK_SOFT}; padding:14px 0; }
#dawncard .dfoot { padding:11px 18px 15px; display:flex; align-items:center; gap:10px; }
#dawncard .dfoot .chip { font-size:11.5px; color:#7a4d12; font-weight:800; background:rgba(120,80,34,.12); border:1px solid ${SEP}; border-radius:12px; padding:3px 9px; }
#dawncard .dfoot button { margin-left:auto; height:36px; padding:0 20px; border-radius:8px; border:1px solid #6e1616; background:linear-gradient(180deg,#a83030,#7a1c1c); color:#f7e9c8; font-family:${SERIF}; font-weight:800; font-size:13px; cursor:pointer; }
`;

function esc(s: string): string { return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!)); }
function away(s: number): string {
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))} minutes`;
  if (s < 86400) return `${Math.round(s / 3600)} hours`;
  return `${Math.round(s / 86400)} days`;
}

export class DawnReport {
  private wrap: HTMLElement;
  private shown = false;

  constructor(root: HTMLElement, private readonly audio: Audio) {
    const style = document.createElement('style'); style.textContent = CSS; document.head.appendChild(style);
    const wrap = document.createElement('div'); wrap.id = 'dawnwrap';
    wrap.innerHTML = `<div id="dawncard"></div>`;
    root.appendChild(wrap); this.wrap = wrap;
    wrap.addEventListener('click', (e) => { if (e.target === wrap || (e.target as HTMLElement).dataset.x) this.close(); });
  }

  /** Called each frame: show the report the first time the server sends it. */
  maybeShow(net: Net): void {
    if (this.shown || !net.dawn) return;
    const d = net.dawn; net.dawn = null; this.shown = true;
    const card = this.wrap.querySelector('#dawncard') as HTMLElement;
    const lines = d.lines.length
      ? d.lines.map((l) => `<div class="dline">${esc(l)}</div>`).join('')
      : `<div class="dnone">Calm seas — nothing stirred while you were away.</div>`;
    card.innerHTML =
      `<div class="dh"><span class="seal"></span><b>The Dawn Report</b></div>` +
      `<div class="dsub">Welcome back, Captain — you were away ${away(d.awayS)}.</div>` +
      `<div class="dbody">${lines}</div>` +
      `<div class="dfoot">${d.streak > 0 ? `<span class="chip">🔥 Day ${d.streak} streak</span>` : ''}<span class="chip">🎖 Season Lv ${d.seasonLevel}</span><button data-x="1">Set Sail</button></div>`;
    this.wrap.classList.add('on');
    this.audio.pageTurn();
  }

  private close(): void { this.wrap.classList.remove('on'); this.audio.ui(); }
}
